import { resolveConfigPaths, type Config } from '@/src/utils/get-config'
import { getPackageManager } from '@/src/utils/get-package-manager'
import { getProjectConfig } from '@/src/utils/get-project-info'
import { handleError } from '@/src/utils/handle-error'
import { logger } from '@/src/utils/logger'
import * as templates from '@/src/utils/templates'
import { transform } from '@/src/utils/transformers'
import { transformTailwindConfig } from '@/src/utils/transformers/transform-tailwind-config'
import { preFlight } from '@/src/utils/preflight'
import * as ERRORS from '@/src/utils/errors'
import chalk from 'chalk'
import { Command } from 'commander'
import { execa } from 'execa'
import { existsSync, promises as fs } from 'fs'
import ora from 'ora'
import path from 'path'
import * as z from 'zod'
import { getCreateNextAppCommand, getNpxCommand } from '../utils/npm-commands'
import { createProject } from '../utils/create-project'
import { getRegistryBaseColor } from '../utils/registry'
import { runAdd } from './add'
import { PROJECT_DEPENDENCIES } from '@/src/commands/init'

const createOptionsSchema = z.object({
  cwd: z.string(),
  yes: z.boolean(),
  token: z.string().optional(),
})

export const create = new Command()
  .name('create')
  .description('(experimental) create a new Next.js project with component id')
  .argument('id', 'the component id to add')
  .option('-y, --yes', 'skip confirmation prompt.', false)
  .option(
    '-c, --cwd <cwd>',
    'the working directory. defaults to the current directory.',
    process.cwd()
  )
  .option(
    '-t, --token <token>',
    'unique token to authenticate for private components.'
  )
  .action(async (id, opts) => {
    await runCreate(id, opts)
  })

export async function runCreate(
  id: string,
  opts: z.infer<typeof createOptionsSchema>
) {
  try {
    const options = createOptionsSchema.parse(opts)
    let cwd = path.resolve(options.cwd)

    const packageManager = await getPackageManager(cwd, {
      withFallback: true,
    })

    const { projectPath } = await createProject(cwd)
    cwd = projectPath
    process.chdir(cwd)

    const error = await preFlight(cwd)

    if (error === ERRORS.MISSING_DIR) {
      logger.error(
        `The path ${cwd} does not exist. Make sure the path is correct.`
      )
      process.exit(1)
    }

    if (error === ERRORS.EXISTING_CONFIG) {
      logger.error(
        `The path ${cwd} already contains a components.json file. Remove this file and try again.`
      )
      process.exit(1)
    }

    if (error === ERRORS.NON_NEXT_PROJECT) {
      logger.warn(`The v0 cli only supports Next.js projects for now.`)
      logger.warn(
        `If you're using a different framework, you can copy and paste the generated code into your app.`
      )
      logger.warn('')
      process.exit(1)
    }

    if (error === ERRORS.TAILWIND_CONFIG_MISSING) {
      logger.warn(
        `Tailwind CSS is not installed. Make sure to install Tailwind CSS in your project first.`
      )
      logger.warn(
        `Visit https://tailwindcss.com/docs/guides/nextjs to get started.`
      )
      logger.warn('')
      process.exit(1)
    }

    if (error === ERRORS.EMPTY_PROJECT) {
      logger.error(`Looks like you're trying to initialize an empty project.`)
      logger.error(
        `Create a new Next.js project first and then run the init command.`
      )

      const createNextAppCommand = getCreateNextAppCommand(packageManager)
      const initCommand = getNpxCommand(packageManager, 'v0@latest init')
      logger.info('')
      logger.info(` ${chalk.green(createNextAppCommand)}`)
      logger.info(` ${chalk.green(initCommand)}`)
      logger.info('')
      process.exit(1)
    }

    const projectConfig = await getProjectConfig(cwd)

    if (!projectConfig) {
      logger.error(
        `Failed to read project configuration. Make sure your project is a Next.js project.`
      )
      process.exit(1)
    }

    const config: Config = await resolveConfigPaths(cwd, projectConfig)

    // Write to file.
    let spinner = ora(`Writing components.json...`).start()
    const targetPath = path.resolve(cwd, 'components.json')
    await fs.writeFile(
      targetPath,
      JSON.stringify(projectConfig, null, 2),
      'utf8'
    )
    spinner.succeed()

    spinner = ora(`Initializing project...`).start()

    // Ensure all resolved paths directories exist.
    for (const [key, resolvedPath] of Object.entries(config.resolvedPaths)) {
      // Determine if the path is a file or directory.
      // TODO: is there a better way to do this?
      let dirname = path.extname(resolvedPath)
        ? path.dirname(resolvedPath)
        : resolvedPath

      // If the utils alias is set to something like "@/lib/utils",
      // assume this is a file and remove the "utils" file name.
      // TODO: In future releases we should add support for individual utils.
      if (key === 'utils' && resolvedPath.endsWith('/utils')) {
        // Remove /utils at the end.
        dirname = dirname.replace(/\/utils$/, '')
      }

      if (!existsSync(dirname)) {
        await fs.mkdir(dirname, { recursive: true })
      }
    }

    const extension = config.tsx ? 'ts' : 'js'

    // Read tailwind config.
    const tailwindConfig = await fs.readFile(
      config.resolvedPaths.tailwindConfig,
      'utf8'
    )

    let updatedTailwindConfig: string
    try {
      updatedTailwindConfig = await transform(
        {
          filename: config.resolvedPaths.tailwindConfig,
          raw: tailwindConfig,
          config,
        },
        [transformTailwindConfig]
      )
    } catch (e) {
      if (e instanceof Error && e.message === 'property-exists') {
        logger.error(
          `v0 now requires tailwind modifications it was unable to perform automatically.\nPlease re-run the \`v0 init\` command to reset your tailwind configuration.`
        )
        process.exit(1)
      } else {
        throw e;
      }
    }

    // Write tailwind config.
    await fs.writeFile(
      config.resolvedPaths.tailwindConfig,
      updatedTailwindConfig,
      'utf8'
    )

    const baseColor = await getRegistryBaseColor(config.tailwind.baseColor)

    let cssFilePayload = config.tailwind.cssVariables
      ? baseColor.cssVarsTemplate
      : baseColor.inlineColorsTemplate

    // Clean up.
    cssFilePayload = cssFilePayload
      .trim()
      .split('\n')
      .map(line => line.trim())
      .join('\n')

    if (baseColor) {
      await fs.writeFile(
        config.resolvedPaths.tailwindCss,
        cssFilePayload,
        'utf8'
      )
    }

    // Write cn file.
    await fs.writeFile(
      `${config.resolvedPaths.utils}.${extension}`,
      extension === 'ts' ? templates.UTILS : templates.UTILS_JS,
      'utf8'
    )

    spinner.succeed()

    // Install dependencies.
    const dependenciesSpinner = ora(`Installing dependencies...`).start()

    // TODO: add support for other icon libraries.
    const deps = [
      ...PROJECT_DEPENDENCIES,
      // v0 uses default style for generation and new-york uses radix icons.
      // The icons vector however uses lucide.
      // We need to install both.
      '@radix-ui/react-icons',
      'lucide-react',
    ]

    await execa(
      packageManager,
      [packageManager === 'npm' ? 'install' : 'add', ...deps],
      {
        cwd,
      }
    )
    dependenciesSpinner.succeed()

    const componentSpinner = ora(`Adding components...`).start()
    await runAdd(id, {
      cwd,
      yes: true,
      overwrite: true,
      name: 'Component',
      silent: true,
      token: options.token,
    })
    componentSpinner.succeed()

    // Overwrite app/page.tsx with the new import.
    const pagePath = path.resolve(cwd, 'app', 'page.tsx')
    await fs.writeFile(
      pagePath,
      `import { Component } from '@/components/component'\n\nexport default function Home() {\n  return <Component />\n}`,
      'utf8'
    )

    const devCommand =
      packageManager === 'npm' ? 'npm run dev' : `${packageManager} dev`

    logger.log('')
    logger.log(chalk.green('Success! Your project is ready.'))
    logger.log(chalk.green("We've also added your component and dependencies."))
    logger.log('')
    logger.log('- To start the development server:')
    logger.log('')
    logger.log(`    cd ${cwd}`)
    logger.log(`    ${devCommand}`)
    logger.log('')
    logger.log('- To deploy your project to ▲ Vercel:')
    logger.log('')
    logger.log(`    cd ${cwd}`)
    logger.log(`    vercel deploy`)
    logger.log('')
  } catch (error) {
    handleError(error)
  }
}
